// Ref-counted registry of orders whose card (or page) is currently on screen.
// The same order can be shown by more than one surface at once (e.g. the
// activity sheet AND listing details), so we count rather than use a boolean —
// one surface unmounting must not mark the order hidden while another still
// shows it. Updated by `useOrderVisibility`; read by the toast layer so a toast
// isn't made sticky for an order the user is already looking at.
const visibleCounts = new Map<string, number>();

/** True while at least one on-screen surface is showing this order. */
export function isOrderVisible(orderId: string): boolean {
  return (visibleCounts.get(orderId) ?? 0) > 0;
}

export function incrementOrderVisible(orderId: string): void {
  visibleCounts.set(orderId, (visibleCounts.get(orderId) ?? 0) + 1);
}

export function decrementOrderVisible(orderId: string): void {
  const next = (visibleCounts.get(orderId) ?? 0) - 1;
  if (next <= 0) visibleCounts.delete(orderId);
  else visibleCounts.set(orderId, next);
}

// Latest-mounted card element per order, for tap-to-scroll. Last write wins;
// unregister only clears the entry if it still points at this element (so one
// card unmounting doesn't wipe another card of the same order).
const orderCardEls = new Map<string, HTMLElement>();

export function registerOrderCardEl(orderId: string, el: HTMLElement): void {
  orderCardEls.set(orderId, el);
}

export function unregisterOrderCardEl(orderId: string, el: HTMLElement): void {
  if (orderCardEls.get(orderId) === el) orderCardEls.delete(orderId);
}

/**
 * If this order's card is mounted on the active, rendered page, scroll it into
 * view and return true. Returns false when there's no card or it sits on a
 * backgrounded page (Ionic hides those with display:none → offsetParent null),
 * so the caller can fall back to opening the activity sheet.
 */
export function scrollToOrderCard(orderId: string): boolean {
  const el = orderCardEls.get(orderId);
  if (!el || !el.isConnected || el.offsetParent === null) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
}
